/**
 * ManageSubmission component is a crucial part of the student interface for submitting and managing their code submissions.
 * It provides two options for code submission:
 * 1. Uploading a code file.
 * 2. Writing code directly in the integrated code editor.
 *
 * Key Features:
 * - Students can view previous submissions and select one to see its details.
 * - Students can run their code in the editor without submitting it.
 * - Custom input execution is supported to test code with user-provided input.
 * - The component displays compiler errors, test results, and execution status of submissions.
 * - Students can see marks obtained in both visible and hidden test cases.
 * - The interface includes a dropzone for file uploads, a code editor, and various controls for managing submissions.
 * - Supports running code with custom inputs and viewing the output directly.
 * - Provides options to zoom in/out and toggle fullscreen mode for the code editor.
 * - Displays detailed feedback on test results, including marks and any compiler errors.
 */

import log from "loglevel";
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

// Third-party libraries
import Dropzone from "react-dropzone";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import JSZip from "jszip";
import ReactPaginate from "react-paginate";

// React Bootstrap components
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import { Card } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import Modal from "react-bootstrap/Modal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { Spinner } from "react-bootstrap";
import { Table } from "react-bootstrap";
import { Tabs, Tab } from "react-bootstrap";
import Tooltip from "react-bootstrap/Tooltip";

// Icons
import { AiOutlinePlayCircle, AiOutlineRetweet } from "react-icons/ai";
import { CgRedo } from "react-icons/cg";
import { FaEdit } from "react-icons/fa";
import { IoMdDownload } from "react-icons/io";
import { MdDarkMode, MdLightMode, MdOutlineFullscreen, MdOutlineFullscreenExit, MdOutlineZoomIn, MdOutlineZoomOut } from "react-icons/md";
import { VscRunAll } from "react-icons/vsc";
// import { FiSettings, FiMinus } from "react-icons/fi";

// Local utilities and services
import * as constants from "Constants";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import httpService from "services/httpService.js";

// Local components
import MonacoEditorComponent from "./LazyLoadMonacoEditor";
import RunTerminal from "./RunTerminal";

// Redux actions
import { setChatbotExternalData } from "../../../../../../../Store/Chatbot/Action.js";
import { updateSelectedSubmission } from "../../../../../../../Store/Programming/Student/Actions";

// Styles
import "./ManageSubmissions.css";

const RUNTIME_ERROR_FALLBACK =
  "Your program did not run to completion, so no marks were awarded.";

// Runtime error descriptor for one testcase set, or null when that set is fine.
// resultRows keep the check scoped to the polled set; the submission-level status covers both sets.
const buildRuntimeError = (executionStatus, errorText, resultRows) => {
  const isTle = executionStatus === "TLE";
  // A crash can leave no stderr behind, so a non-zero return_code is error evidence on its own.
  const crashedRow = (resultRows ?? []).find(
    (row) => row.return_code || row.verdict
  );
  if (!isTle && executionStatus !== "EXEC_ERR" && !crashedRow && !errorText) {
    return null;
  }
  return {
    label: isTle
      ? "Time Limit Exceeded"
      : crashedRow?.verdict || "Runtime Error",
    text: errorText || RUNTIME_ERROR_FALLBACK,
  };
};

export default function ManageSubmission({
  programId,
  changeAccess,
  access,
  submission,
  setSelectedSubmission,
  setTestResults,
  delegate_student_id,
  instructorAdditionalFilesContent,
  activityTitle,
  initialCode,
  setInitialCode,
  setFileName,
  practiceMarks,
  evaluateMarks,
  isTemplateCode,
  setIsTemplateCode,
  setVisibleTestcaseEvaluationResultsStatus,
  setHiddenTestcaseEvaluationResultStatus,
  setAttemptsLeft,
  displayRunWithoutSubmitButton,
}) {
  // Use the useSelector hook to access Redux state
  const studentProgramming = useSelector((state) => state.studentprogramming);
  // Use the useDispatch hook to get the dispatch function
  const dispatch = useDispatch();
  let interval;

  //States for handling the execution of the code on custom input
  //================================================================================================
  const [showPopupCustomInput, setShowPopupCustomInput] = useState(false); // state for showing custom input window
  const [customInput, setCustomInput] = useState(null); // input for custom input execution
  const [showCustomInputCard, setShowCustomInputCard] = useState(false); // to show custom input card(will be visible when student will make a submission)
  const [isFetchingCustom, setIsFetchingCustom] = useState(false); // to show execution status for CustomInputRun
  let [customResult, setCustomResult] = useState(null); // result for custom input

  //================================================================================================
  //States for handling the execution of the code on test cases
  //================================================================================================
  let evaluate_flag = "Evaluation in Process";

  const [isFetchingVisible, setIsFetchingVisible] = useState(false); // to show loading symbol for run practice marks
  const [isFetchingHidden, setIsFetchingHidden] = useState(false); // to show loading symbol for run hidden marks
  const [obtainedVisibleMarks, setObtainedVisibleMarks] = useState(null);
  const [obtainedHiddenMarks, setObtainedHiddenMarks] = useState(null);
  const [showPopupCompilerError, setShowPopupCompilerError] = useState(false); // state for showing compiler error window
  const [compilerErrors, setCompilerErrors] = useState(null); // compiler errors for the selected submission
  const [showPopupRuntimeError, setShowPopupRuntimeError] = useState(false); // state for showing runtime error window
  // Runtime errors are per testcase-set, unlike compilation errors: practice can pass while evaluation crashes.
  const [visibleRuntimeError, setVisibleRuntimeError] = useState(null); // { label, text } for practice testcases
  const [hiddenRuntimeError, setHiddenRuntimeError] = useState(null); // { label, text } for evaluation testcases
  const [activeRuntimeError, setActiveRuntimeError] = useState(null); // whichever the student clicked
  const [disableCodingActions, setDisableCodingActions] = useState(false); // to disable coding actions when the code is being evaluated
  const [customEvaluationMessage, setCustomEvaluationMessage] = useState(""); // to show messages related to custom input execution
  const [uploading, setUploading] = useState(false); // to show uploading status when student uploads a file for submission

  //================================================================================================

  //States for handling file upload
  //================================================================================================
  let [selectedFiles, setSelectedFiles] = useState([]); // files chosen by student to make a submission
  const [fileSize, setFileSize] = useState(true); // states to handle file issues
  const [_fileUploadProgress, setFileUploadProgress] = useState(false);
  const [fileUploadResponse, setFileUploadResponse] = useState(null);
  const [Expected_Submission_File, setExpectedSubmissionFile] = useState(""); // State for storing allowed file names
  const [isFirstPageLoad, setIsFirstPageLoad] = useState(true);
  //================================================================================================

  //States for handling the previous submissions
  //================================================================================================
  const [showMaxAttemptsModal, setShowMaxAttemptsModal] = useState(false);
  const [showTimeExpiredModal, setShowTimeExpiredModal] = useState(false);
  const [showIPBlockedModal, setShowIPBlockedModal] = useState(false);
  const [submissions, setSubmissions] = useState(null); // list of submissions by the student
  const [showPopupSubmissions, setShowPopupSubmissions] = useState(false); // state for showing submission window
  const [selectedPopUpSubmission, setSelectedPopUpSubmission] = useState(null); // selected submission from the pop-up
  const [pageCount, setPageCount] = useState(0); // used in the pagination, it is the total number of pages required to display all the records.
  const [sizePage, setSizePage] = useState(10); // used in the pagination, sizePage is the number of records we want to show per page
  const [evaluationStatus, setEvaluationStatus] = useState(null);
  const [isGetLatest, setIsGetLatest] = useState(true);
  const [fileWasUploaded, setFileWasUploaded] = useState(false);

  //================================================================================================

  //States for handling the code editor
  //================================================================================================
  const initialEditorText = {};

  //States for handling template code feature
  //================================================================================================
  const [markersMapping, setMarkersMapping] = useState([]);
  const [lastLine, setLastLine] = useState();
  const [instructorFileLastLineNumber, setInstructorFileLastLineNumber] = useState(25);
  const [monacoInstance, setMonacoInstance] = useState(null);
  const [change, setChange] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(null);
  const [showConfirmSubmissionModal, setShowConfirmSubmissionModal] = useState(false);
  const [showFinalCompilableCode, setShowFinalCompilableCode] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [finalCompilableCode, setFinalCompilableCode] = useState(null);
  const [showFailedFinalCompilableCode, setShowFailedFinalCompilableCode] = useState(false);
  const [prevFileInEditor, setPrevFileInEditor] = useState(submission?.files_to_be_submitted[0])

  const headerFileToBeSubmitted = submission.header_files_to_be_submitted.map(
    (file) => file + ".h"
  );
  //Initiliasing a dictionary with key as file name and value as empty string for the editor
  submission.files_to_be_submitted.forEach((file) => {
    initialEditorText[file] = "";
  });
  //Initiliasing a dictionary with key as header file name and value as empty string for the editor
  headerFileToBeSubmitted.forEach((file) => {
    initialEditorText[file] = "";
  });
  const [language, setLanguage] = useState(submission?.program_language[0]); // default language for editor
  const [editorText, setEditorText] = useState(initialEditorText);
  const [currentFileinEditor, setCurrentFileinEditor] = useState(
    submission.files_to_be_submitted[0]
  );

  const prevEditorTextRef = useRef(editorText);
  // Ref to store the editor instance
  const editorRef = useRef(null);
  // Flag to prevent unnecessary updates to the editor text
  const ignoreChange = useRef(false);
  const latestChangeRef = useRef(null);

  const languageToFileExtension = {
    C: "c",
    "C++": "cpp",
    Python3: "py",
    Simplecpp: "cpp",
    Java: "java",
  };
  const languageToSyntaxHighlighting = {
    C: "c",
    "C++": "cpp",
    Python3: "python",
    Simplecpp: "cpp",
    Java: "java",
  };

  const [mode, setMode] = useState('light');
  //States for handling full screen and font size of the code editor
  const [fontSize, setFontSize] = useState(14); // Default font size
  const [isFullScreen, setisFullScreen] = useState(false);
  const handle = useFullScreenHandle();

  //States for handling the if the current file in Editor is an instructor file
  const [isInstructorFile, setIsInstructorFile] = useState(false);


  //================================================================================================

  //States for handling auto save feature
  //================================================================================================
  const [sessionKey, setSessionKey] = useState(""); //To save the sessionkey for autosaving
  const [lastSavedTime, setLastSavedTime] = useState(new Date());
  const [showEditorPopup, setShowEditorPopup] = useState(false); // state for showing editor popup

  //================================================================================================
  //States for run without submit feature
  //================================================================================================
  const [errorOnRun, setErrorOnRun] = useState("");
  const [successOnRun, setSuccessOnRun] = useState(false);
  const [runToggle, setRunToggle] = useState(false);
  const [isRunLoading, setIsRunLoading] = useState(false);
  //================================================================================================

  //States for handling submission and runwithoutsubmit attempts
  const [toggle, setToggle] = useState(false);

  // This array stores files to be submitted and additional files provided by the instructor
  const allFiles = [
    ...submission.files_to_be_submitted,
    ...(instructorAdditionalFilesContent[language]
      ? Object.keys(instructorAdditionalFilesContent[language])
      : []),
  ];

  // common utility function to apply decorations 
  const applyDecorations = (markersMapping, editorRef) => {
    // Combining all decorations in one array
    let prevEnd = 0;
    const allDecorations = markersMapping?.flatMap(([startLine, endLine]) => {

      if (!monacoInstance) {
        return [];
      }
      const decorations = [];

      // Read-only section before editable section
      if (prevEnd < startLine) {
        decorations.push({
          range: new monacoInstance.Range(prevEnd, 1, startLine, 1),
          options: {
            isWholeLine: true,
            className: "custom-region-read_only",
          },
        });
      }

      // Editable section
      decorations.push({
        range: new monacoInstance.Range(startLine + 1, 1, endLine - 1, 1),
        options: {
          isWholeLine: true,
          className: "custom-region-editable",
        },
      });

      prevEnd = endLine;
      return decorations;
    });

    // Last read-only section
    if (prevEnd <= lastLine && markersMapping?.length !== 0) {
      allDecorations.push({
        range: new monacoInstance.Range(prevEnd, 1, lastLine, 1),
        options: {
          isWholeLine: true,
          className: "custom-region-read_only",
        },
      });
    }

    // Applying all decorations in one call
    editorRef?.current?.deltaDecorations([], allDecorations);
  }

  //To catch the ResizeObserver error and hide the error message
  //during exit from Code Editor Full Screen mode.
  useEffect(() => {
    setToggle(!toggle);
    const errorHandler = (e) => {
      if (
        e.message.includes(
          "ResizeObserver loop completed with undelivered notifications" ||
          "ResizeObserver loop limit exceeded"
        )
      ) {
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        if (resizeObserverErr) {
          resizeObserverErr.style.display = "none";
        }
      }
    };
    window.addEventListener("error", errorHandler);
    return () => {
      window.removeEventListener("error", errorHandler);
      if (editorRef.current?._changeListener) {
        editorRef.current._changeListener.dispose();
      }
      if (editorRef?.current?._cursorListener) {
        editorRef?.current._cursorListener.dispose();
      }
    };
  }, []);
  useEffect(() => {
    const fetchAttemptsLeft = async () => {
      const url = constants.base_url + "/evalpro/get_attempts_left/" + programId;
      try {
        const response = await httpService.get(url);
        setAttemptsLeft(response.data);
      } catch (error) {
        log.error(error);
      }
    }
    fetchAttemptsLeft();
  }, [toggle]);
  useEffect(() => {
    if (programId) {
      // var currentSubmission = "";
      const fetchLatestSubmissionBeforeEditorLoad = async () => {
        // Construct the URL for fetching the latest submission
        var url = constants.base_url + "/evalpro/get_latest/" + programId;
        var data = {};
        if (delegate_student_id) {
          data = { ...data, student_id: delegate_student_id };
        }
        try {
          const response = await httpService
            .get(url, {
              headers: {
                "Content-Type": "application/pdf",
              },
              params: data,
            })
          // Dispatch an action to update the selected submission in the Redux store
          dispatch(
            updateSelectedSubmission(response.data.student_submission.id)
          );

          changeAccess(response.data.access_control_vars);

          if (Object.keys(response.data.student_submission).length !== 0) {
            // Set the latest submission as the selected submission
            setSelectedSubmission(response.data.student_submission);
            setSelectedPopUpSubmission(response.data.student_submission);

            // If execution of visible test cases is done, set the obtained marks
            if (
              !response.data.student_submission
                .execution_pending_for_visible_testcases
            ) {
              setObtainedVisibleMarks(
                response.data.student_submission.obtained_visible_marks
              );
            }

            // If execution of hidden test cases is done, set the obtained marks
            if (
              !response.data.student_submission
                .execution_pending_for_hidden_testcases
            ) {
              setObtainedHiddenMarks(
                response.data.student_submission.obtained_hidden_marks
              );
            }
            // Set the compiler errors
            setCompilerErrors(response.data.student_submission.compiler_errors);

            // Resolved per set, so a crash in one never lights up the other's cell.
            const submission = response.data.student_submission;
            setVisibleRuntimeError(
              buildRuntimeError(
                submission.visible_testcases_execution_status,
                submission.visible_runtime_errors
              )
            );
            setHiddenRuntimeError(
              buildRuntimeError(
                submission.hidden_testcases_execution_status,
                submission.hidden_runtime_errors
              )
            );
          }
        }
        catch (error) {
          log.error(error);
        }
      };
      fetchLatestSubmissionBeforeEditorLoad()

      const fetchEditorBufferBeforeEditorLoad = async () => {
        // Construct the URL for fetching the editor data
        var url = constants.base_url + "/evalpro/editor/" + programId;
        var data = {};
        if (delegate_student_id) {
          data = { ...data, student_id: delegate_student_id };
        }
        try {
          const response = await httpService
            .get(url, {
              headers: {
                "Content-Type": "application/pdf",
              },
              params: data,
            });

          // If the response indicates a failure, it means that the buffer is not present for the student for this activity.
          //In this case, we need to create a new buffer for the student. post request is made to create a new buffer for the student.
          if (response.data.status == "fail") {
            console.log("editor response 1", response.data);

            let form_data = new FormData();
            form_data.append("buffer_data", JSON.stringify(editorText));
            if (delegate_student_id) {
              form_data.append("student_id", delegate_student_id);
            }
            httpService
              .post(url, form_data, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              })
              .then((response) => {
                // Set the session key from the response
                setSessionKey(response.data.editor_buffer.session_key);
              });
          } else {
            // If the response indicates success, update the editor text with the latest buffer data
            setSessionKey(response.data.editor_buffer.session_key);
            // Updates the editor text with the latest buffer data
            const updatedEditorText = {
              ...editorText,
              ...Object.keys(editorText).reduce((acc, key) => {
                if (key in response.data.editor_buffer.buffer_data) {
                  acc[key] = response.data.editor_buffer.buffer_data[key];
                }
                return acc;
              }, {}),
            };
            setInitialCode(response?.data?.student_preview);
            setEditorText(updatedEditorText);
            setIsTemplateCode(response?.data?.is_template_coding_activity);
          }
        }
        catch (error) {
          log.error(error);
        }
      };
      fetchEditorBufferBeforeEditorLoad();
    }

    // Construct the expected submission file names based on the program language and files to be submitted
    let files_to_be_submitted = submission.files_to_be_submitted;
    let program_language = submission.program_language;
    let cppCount = 0;
    let Expected_Submission_FileNames = "";
    let header_files_to_be_submitted = submission.header_files_to_be_submitted;

    for (let i = 0; i < program_language.length; i++) {
      let lang = program_language[i];
      let extension;

      // Determine the file extension based on the program language
      if (lang === "C++" || lang === "Simplecpp") {
        extension = ".cpp";
      } else if (lang === "C") {
        extension = ".c";
      } else if (lang === "Python3") {
        extension = ".py";
      } else if (lang === "Java") {
        extension = ".java";
      }

      if (extension) {
        // Skip the second occurrence of .cpp for the case of Simplecpp
        if (extension === ".cpp") {
          cppCount++;
          if (cppCount === 2) {
            continue;
          }
        }
        // Construct the expected submission file names
        Expected_Submission_FileNames +=
          (i > 0 ? " or " : "") +
          files_to_be_submitted.map((file) => file + extension).join(",");
        if (header_files_to_be_submitted.length > 0) {
          Expected_Submission_FileNames +=
            "," +
            header_files_to_be_submitted.map((file) => file + ".h").join(",");
        }
      }
    }

    // Set the expected submission file names
    setExpectedSubmissionFile(Expected_Submission_FileNames);
    setFileName(Expected_Submission_FileNames)
  }, []);

  //This useEffect is used to support the auto-save feature in the code editor. This runs whenever the 'editorText' state changes.
  useEffect(() => {
    if (isInstructorFile) {
      const code = instructorAdditionalFilesContent[language][currentFileinEditor];
      setInstructorFileLastLineNumber(code?.split("\n")?.length);
    } else if (isTemplateCode) {
      const codeBuffer = editorText;
      Object.entries(codeBuffer).forEach(([fileName, code]) => {
        if (fileName === currentFileinEditor) {
          const markers = [];
          const startMarkersLineNumbers = [];
          const endMarkersLineNumbers = [];
          const lines = code?.split("\n"); // Split file content into lines
          lines?.forEach((line, index) => {
            if (line.includes("/* --- Start of student code, Don't Remove or edit these markers --- */")) {
              startMarkersLineNumbers.push(index + 1);
            }
            if (line.includes("/* --- End of student code, Don't Remove or edit these markers --- */")) {
              endMarkersLineNumbers.push(index + 1);
            }
          });
          setLastLine(lines?.length,);
          if (startMarkersLineNumbers?.length !== endMarkersLineNumbers?.length) {
            return;
          }
          for (let i = 0; i < startMarkersLineNumbers?.length; i++) {
            markers.push([startMarkersLineNumbers[i], endMarkersLineNumbers[i]])
          }
          //updating markers as soon as text in the editor is changed
          setMarkersMapping(markers);
        }
      });
    }
    if (access.enable_upload_submission) {
      const currentTime = new Date();

      // If more than 5 seconds have passed since the last save
      if (currentTime - lastSavedTime > 5000) {
        const url = constants.base_url + "/evalpro/editor/" + programId;
        const form_data = new FormData();
        form_data.append("buffer_data", JSON.stringify(editorText));
        form_data.append("session_key", sessionKey);
        if (delegate_student_id) {
          form_data.append("student_id", delegate_student_id);
        }
        httpService
          .put(url, form_data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            // If the server response indicates that the code has changed
            if (response.data.status === "code_changed") {
              // Show the editor popup
              setShowEditorPopup(true);
            }
            // Update 'prevEditorTextRef' with the current 'editorText' state
            prevEditorTextRef.current = editorText;
          });

        // Update 'lastSavedTime' with the current time
        setLastSavedTime(currentTime);
      }

      // Set an interval to check for changes in 'editorText' every 5 seconds
      const interval = setInterval(() => {
        // If less than or equal to 5 seconds have passed since the last save
        // and 'editorText' has changed since the last save
        if (
          currentTime - lastSavedTime <= 5000 &&
          prevEditorTextRef.current !== editorText
        ) {
          const url = constants.base_url + "/evalpro/editor/" + programId;
          const form_data = new FormData();
          form_data.append("buffer_data", JSON.stringify(editorText));
          form_data.append("session_key", sessionKey);
          if (delegate_student_id) {
            form_data.append("student_id", delegate_student_id);
          }
          httpService
            .put(url, form_data, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then((response) => {
              if (response.data.status === "code_changed") {
                setShowEditorPopup(true);
              }
              prevEditorTextRef.current = editorText;
            });

          // Update 'lastSavedTime' with the current time
          setLastSavedTime(currentTime);
        }
      }, 5000);

      return () => clearInterval(interval);
    }

  }, [editorText, monacoInstance, currentFileinEditor, latestChangeRef.current]);

  //this useEffect is used to apply the decorations to the text in code editor whenever markers are updated or monaco instance is changed
  useEffect(() => {

    if (!editorRef.current || !monacoInstance || !markersMapping) {
      return;
    }

    if (isInstructorFile) {
      const decorations = [];
      decorations.push({
        range: new monacoInstance.Range(0, 1, instructorFileLastLineNumber, 1),
        options: {
          isWholeLine: true,
          className: "custom-region-read_only",
        },
      })
      //applying decorations to instructor additional file
      editorRef.current.deltaDecorations([], decorations);
    } else {
      applyDecorations(markersMapping, editorRef);
    }
  }, [markersMapping, monacoInstance, currentFileinEditor]);

  //useEffect to Restrict user from making changes in read-only sections on initial render of code in code editor
  useEffect(() => {
    if (!editorRef.current || markersMapping?.length === 0 || !isTemplateCode) return;
    const editor = editorRef.current;
    const previousMarkers = markersMapping;
    const {
      startLineNumber: changeStartLineNumber,
      endLineNumber: changeEndLineNumber,
      endColumn: changeEndColumn,
    } = latestChangeRef?.current?.range || {};

    const isChangeAllowed = markersMapping.some(
      ([start, end]) =>
        changeStartLineNumber > start &&
        changeEndLineNumber < end
    );

    if (!isChangeAllowed) {
      if (change?.[0].text?.length > 5) return;

      setMarkersMapping(previousMarkers);

      // Backspace at the start of an editable block
      const backSpaceAtStart = markersMapping.some(([start]) => {
        return (
          changeStartLineNumber === start &&
          changeEndColumn === 1 &&
          change?.[0].text === ""
        );
      });

      if (
        change?.[0].text?.length === 1 ||
        change?.[0].text.includes('\n') ||
        backSpaceAtStart
      ) {
        if (prevFileInEditor !== currentFileinEditor) {
          return;
        } else {
          displayGlobalMessage("Don't make any changes in the instructor provided code", "error");
          editor.trigger('keyboard', 'undo', null);
          editor.setPosition({ lineNumber: markersMapping?.[0][0] + 1, column: 1 });
          setMarkersMapping(previousMarkers);
          editor.trigger('keyboard', 'type', { text: " " });
          applyDecorations(previousMarkers, editorRef);
        }
      }
    }
  }, [editorRef.current, markersMapping, change, monacoInstance]);

  //useEffect to handle cursor position in code editor 
  useEffect(() => {
    if (!editorRef.current || markersMapping?.length === 0 || !isTemplateCode) return;
    const {
      startLineNumber: changeStartLineNumber,
      endLineNumber: changeEndLineNumber
    } = latestChangeRef?.current?.range || {};
    const editor = editorRef?.current;
    const line = cursorPosition?.lineNumber;


    let isInEditableRange = false;
    let prevEnd = 0;

    for (const [start, end] of markersMapping) {
      if (
        (line > start && line < end) ||
        (line === end && latestChangeRef?.current?.text.includes('\n')) ||
        (changeStartLineNumber === changeEndLineNumber && latestChangeRef?.current?.text === "") ||
        (changeStartLineNumber === changeEndLineNumber && latestChangeRef?.current?.text.includes('\n'))
      ) {
        isInEditableRange = true;
      }
      if (line <= start && line >= prevEnd) {
        isInEditableRange = false;
        break;
      }
      prevEnd = end;
    }
    if (line >= markersMapping[markersMapping.length - 1][1]) {
      isInEditableRange = false;
    }

    if (!isInEditableRange) {
      let nearestLine;
      for (const [start, end] of markersMapping) {
        nearestLine = start + 1;
        if (line <= start || line === end) break;
      }
      editor?.setPosition({ lineNumber: nearestLine, column: 1 });

    }
  }, [cursorPosition, latestChangeRef.current, markersMapping])


  // This useEffect hook runs whenever the 'selectedPopUpSubmission' state changes
  useEffect(() => {
    if (selectedPopUpSubmission) {
      // If the submission contains more than one file or is a .zip file
      if (
        selectedPopUpSubmission?.names_of_files_submitted?.length > 1 ||
        selectedPopUpSubmission?.file_submitted?.endsWith(".zip")

      ) {
        const fileUrl = selectedPopUpSubmission?.file_submitted.toString();
        const zipUrl = fileUrl.startsWith('http') ? fileUrl : constants.base_url + "/" + fileUrl.substr(1);

        httpService
          .get(zipUrl, {
            responseType: "arraybuffer",
          })
          .then((res) => {
            // Use JSZip to read the .zip file
            JSZip.loadAsync(res.data).then((zip) => {
              // Create a copy of the 'editorText' state
              const updatedEditorText = { ...editorText };

              // Create an array to store promises for reading each file in the .zip
              const promises = [];

              // Iterate over each file in the .zip
              zip.forEach((relativePath, file) => {
                // Create a new promise for reading the file
                promises.push(
                  new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = function (event) {
                      // Get the file name without the extension
                      const fileNameWithoutExtension = relativePath.endsWith(
                        ".h"
                      )
                        ? relativePath
                        : relativePath.substring(
                          0,
                          relativePath.lastIndexOf(".")
                        );

                      // Add the file content to 'updatedEditorText'
                      updatedEditorText[fileNameWithoutExtension] =
                        event.target.result;

                      // Resolve the promise
                      resolve();
                    };

                    // Read the file as text
                    file.async("arraybuffer").then((data) => {
                      reader.readAsText(new Blob([data]));
                    });
                  })
                );
              });

              // When all files have been read
              Promise.all(promises).then(() => {
                if (
                  !isGetLatest ||
                  !access.enable_upload_submission ||
                  fileWasUploaded
                ) {
                  setEditorText(updatedEditorText);
                }

                setLanguage(
                  selectedPopUpSubmission.submission_program_language
                );
              });

              setIsFirstPageLoad(false);
              setFileWasUploaded(false);
            });
            setIsGetLatest(false);
            setFileWasUploaded(false);
          })
          .catch((error) => {
            log.error(error);
          });
      } else {
        // If the submission contains only one file and is not a .zip file
        const singleFileUrl = selectedPopUpSubmission?.file_submitted.toString();
        const url = singleFileUrl.startsWith('http') ? singleFileUrl : constants.base_url + "/" + singleFileUrl.substr(1);
        httpService
          .get(url, {
            headers: {
              "Content-Type": "application/pdf",
            },
          })
          .then((res) => {
            // Get the file name without the extension
            const fileName =
              selectedPopUpSubmission.names_of_files_submitted[0];
            const fileNameWithoutExtension = fileName.substring(
              0,
              fileName.lastIndexOf(".")
            );
            if (
              !isGetLatest ||
              !access.enable_upload_submission ||
              fileWasUploaded
            ) {
              log.log("saving value in editor text");
              setEditorText({ [fileNameWithoutExtension]: res.data });
            }
            setLanguage(selectedPopUpSubmission.submission_program_language);
            setIsGetLatest(false);
            setFileWasUploaded(false);
          })
          .catch((error) => {
            // Log any errors
            log.error(error);
          });
      }
    }
  }, [selectedPopUpSubmission]);

  const modeHandler = () => {
    if (mode === 'light') {
      setMode('dark');
    } else {
      setMode('light');
    }
  }

  const downloadHandler = () => {
    let files = Object.entries(editorText).map(([filename, content]) => ({
      name: filename,
      content: content,
    }));

    if (files.length === 1) {
      // Single file download
      const file = files[0];
      const blob = new Blob([file.content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = file.name + `.${languageToFileExtension[language]}`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // Multiple files: Create ZIP
      const zip = new JSZip();
      files.forEach(({ name, content }) => {
        zip.file(name, content);
      });

      zip.generateAsync({ type: "blob" }).then((zipBlob) => {
        const zipUrl = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = zipUrl;
        a.download = `${activityTitle}.zip`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        URL.revokeObjectURL(zipUrl);
      });
    }
  };

  //Whenever there is a discrepancy in the editor text and the buffer data, a popup will be shown to the student with two options:
  //1. Load the changes from the buffer data
  //2. Discard the changes in the buffer data and continue with the current editor text
  //This function is called when the student clicks on the 'Load Changes' Button in the popup

  const handleLoadChanges = () => {
    const url =
      constants.base_url + "/evalpro/editor/load_changes/" + programId;
    const form_data = new FormData();
    form_data.append("session_key", sessionKey);
    if (delegate_student_id) {
      form_data.append("student_id", delegate_student_id);
    }

    httpService
      .put(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        setEditorText(response.data.editor_buffer.buffer_data);
      });
    // Close the popup
    setShowEditorPopup(false);
  };

  //This function is called when the student clicks on the 'Discard Changes' Button in the popup
  const handleDiscardChanges = async () => {
    const url = constants.base_url + "/evalpro/editor/discard/" + programId;
    const form_data = new FormData();
    form_data.append("buffer_data", JSON.stringify(editorText));
    log.log({ editorText });
    form_data.append("session_key", sessionKey);
    if (delegate_student_id) {
      form_data.append("student_id", delegate_student_id);
    }

    await httpService.put(url, form_data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    // Close the popup
    setShowEditorPopup(false);
  };

  const getFormattedDate = (date, type) => {
    if (type) {
      return new Date(date).toLocaleString();
    } else {
      const dateParts = new Date(date).toLocaleString().split(","); // Splitting the formatted date

      // return formatted date
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>{dateParts[1]}</div>
          <div>{dateParts[0]}</div>
        </div>
      );
    }
  };

  const getSubmissionDate = () => {
    // return submission time if there is submission else ""
    if (selectedPopUpSubmission) {
      return getFormattedDate(selectedPopUpSubmission?.uploaded_on);
    } else {
      return "";
    }
  };

  const getDownloadPath = () => {
    // download path for selected submission

    return (
      constants.base_url + selectedPopUpSubmission?.file_submitted?.toString()
    );

  };

  const checkSubmissionExecutionStatus = (testCaseType) => {
    // To check the status of submission execution for seleted submission
    let url = null;
    let form_data = new FormData();

    /**This `testCaseType` arg can hold three values - visible, hidden and null where null is the case when this executionStatus function
     * is called from RunCustomInput, so based on that setting the URL for getting the latest execution status
     **/
    if (testCaseType) {
      url =
        constants.base_url +
        "/evalpro/" +
        testCaseType +
        "/status/" +
        selectedPopUpSubmission.id;
    } else {
      url =
        constants.base_url +
        "/evalpro/custom_input_status/" +
        selectedPopUpSubmission.id;
      form_data.append("task_id", customResult?.task_id);
      form_data.append("input_file_name", customResult?.input_file_name);
    }
    // meta data for the axios requestExpected_Submission_File
    const _requestMetadata = {
      method: "PUT",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    httpService
      .put(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        setEvaluationStatus(response.data.currentStatus);
        if (testCaseType === "visible") setVisibleTestcaseEvaluationResultsStatus(response.data.currentStatus);
        else if (testCaseType === "hidden") setHiddenTestcaseEvaluationResultStatus(response.data.currentStatus);;
        // if evaluation finished setting the flag as "Evalution finsihed" to stop sending axios request and get the execution results
        if (response.data.stop) {
          evaluate_flag = "Evaluation Finished";

          /**This Button can hold three values - visible, hidden and null where null is the case when this executionStatus function
           *  is called from RunCustomInput, so based on that calling runTestCaseButton or RunCustomInput for getting the latest execution result
           **/
          if (testCaseType) {
            runTestCaseButton(testCaseType);
          } else {
            clearInterval(interval);
            evaluate_flag = "Evaluation in Process"; //Reseting it for upcoming click on Run Button i.e. fresh request

            setEvaluationStatus(null); //Reseting it for upcoming click on Run Button i.e. fresh request
            setCustomResult(response?.data?.result);
            //This flag is used to render the Evaluation Status for asychronous execution; setting it false to unhide the component
            setIsFetchingCustom(false);
          }
          displayGlobalMessage(response.data.currentStatus);
          if (response.data?.result?.status?.toLowerCase() == "pass") {
            displayGlobalMessage("Pass", "success");
          } else if (response.data?.result?.status?.toLowerCase() == "fail") {
            displayGlobalMessage("Fail", "error");
          }
        }
      })
      .catch((error) => {
        log.error("run ", "failed. error: ", error);
      });
  };

  const RunCustomInput = () => {
    // for custom input evaluation
    if (!studentProgramming.selected_submission) {
      displayGlobalMessage("Make a submission First....");
    } else {
      // This flag is used to render the Evaluation Status for asynchronous execution
      setIsFetchingCustom(true);

      const url =
        constants.base_url +
        "/evalpro/run_custom_input/" +
        selectedPopUpSubmission.id;
      let form_data = new FormData();
      form_data.append("input_text", customInput);
      form_data.append("key", "buffer_data");
      httpService
        .put(url, form_data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          setCustomResult(response?.data?.result);
          customResult = response?.data?.result;
        })
        .then(() => {
          if (evaluationStatus === null) {
            setEvaluationStatus("Submission waiting in the queue");
          }

          if (evaluate_flag === "Evaluation in Process") {
            interval = setInterval(() => {
              // time interval between sending evaluation requests 2000ms = 2sec
              checkSubmissionExecutionStatus(null);
            }, 2000);
          }
        })

        .catch((error) => { });
    }
  };

  const runTestCaseButton = (testCaseType) => {
    // if true loading icon will come up and also used to show execution Status
    if (testCaseType === "visible") setIsFetchingVisible(true);
    else if (testCaseType === "hidden") setIsFetchingHidden(true);

    const url =
      constants.base_url +
      "/evalpro/evaluate/" +
      testCaseType +
      "/" +
      selectedPopUpSubmission.id;

    let form_data = new FormData();
    if (delegate_student_id) {
      form_data.append("student_id", delegate_student_id);
    }

    httpService
      .put(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        log.debug("response after clicking run button", response);
        // evaluationStatus == null means evaluate Button has not been clicked so, if the first response is coming that means
        // Submission(Task) has been queued in the celery queue
        if (evaluationStatus === null) {
          setEvaluationStatus("Submission waiting in the queue");
        }

        if (evaluate_flag === "Evaluation in Process") {

          interval = setInterval(() => {
            // time interval between sending evaluation requests 2000ms = 2sec
            checkSubmissionExecutionStatus(testCaseType);
            log.log("evaluation in progress")
          }, 2000);
        }

        if (evaluate_flag === "Evaluation Finished") {
          log.log("evaluation finished")
          clearInterval(interval);

          evaluate_flag = "Evaluation in Process"; // Resetting it for the upcoming click on the Run Button i.e. fresh request
          setEvaluationStatus(null); // Resetting it for the upcoming click on the Run Button i.e. fresh request
          if (testCaseType === "hidden") {
            setObtainedHiddenMarks(response.data.obtained_marks);
            setTimeout(() => {
              evaluateMarks?.current?.click();
              log.log("evaluate marks", evaluateMarks)
            }, 100)
          }
          if (testCaseType === "visible") {
            setObtainedVisibleMarks(response.data.obtained_marks);
            setTimeout(() => {
              practiceMarks?.current?.click();
              log.log("practice marks", practiceMarks)
            }, 100)
          }
          changeAccess(response.data.access_control_vars);
          setIsFetchingHidden(false);
          setIsFetchingVisible(false);
          setSelectedSubmission(selectedPopUpSubmission);
          setCompilerErrors(response.data.testcase_details[0]?.compiler_errors);
        }
      })
      .catch((error) => {
        log.error('run button click error: ', error);
        displayGlobalMessage("Some error occurred!", 'error');
        setIsFetchingVisible(false);
        setIsFetchingHidden(false);

        log.error("run ", "failed. error: ", error);
        // If there is an error in the request, stop the interval and reset the flags and states
        log.debug('evaluate flag value in catch block: ', evaluate_flag);
        if (evaluate_flag === "Evaluation Finished") {
          console.log("evaluation finished")
          clearInterval(interval);

          evaluate_flag = "Evaluation in Process"; // Resetting it for the upcoming click on the Run Button i.e. fresh request
          setEvaluationStatus(null); // Resetting it for the upcoming click on the Run Button i.e. fresh request
          if (testCaseType === "hidden") {
            setObtainedHiddenMarks(0);
          }
          if (testCaseType === "visible") {
            setObtainedVisibleMarks(0);
          }
        }
      });
  };

  /**
   * new run test case function for custom input and visible and hidden test cases, this will be called when the student clicks on the Run Button in the submission details popup
   * @param {string} testCaseType 'visible' | 'hidden'
   * Author: Aditya Upadhye
   */
  const runTestCases = (testCaseType) => {

    // disable all coding actions
    setDisableCodingActions(true);

    if (testCaseType === "visible") {
      setVisibleTestcaseEvaluationResultsStatus("Evaluation in Process");
      setIsFetchingVisible(true);
    } else if (testCaseType === "hidden") {
      setHiddenTestcaseEvaluationResultStatus("Evaluation in Process");
      setIsFetchingHidden(true);
    }

    const url =
      constants.base_url +
      "/evalpro/evaluate/" +
      selectedPopUpSubmission.id + "/"
      ;

    // add request body
    let requestBody = {
      'testcases': testCaseType,
      'program_id': -1 // currently dummy
    };

    // let form_data = new FormData();
    // if (delegate_student_id) {
    //   form_data.append("student_id", delegate_student_id);
    // }

    // set custom message for starting evaluation
    setCustomEvaluationMessage("Starting evaluation...");

    httpService
      .post(url, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {

        log.debug("Response after evaluation request: ", response);
        // changeAccess(response?.data?.access_control_vars); // updating access control variables

        // fetch the task ID from response
        let taskId = response?.data?.task_id;

        let submissionId = response?.data?.submission_id;

        const isTestCaseVisible = testCaseType === "visible" ? true : false;

        // taskId == DONE means evaluation is done and results are ready to be fetched
        // if (taskId === "DONE") {
        //   // TODO: fetch the results and update the UI accordingly
        //   checkExecutionStatus(taskId, submissionId, isTestCaseVisible);
        //   return;
        // }

        checkExecutionStatus(taskId, submissionId, isTestCaseVisible);



      })
      .catch((error) => {
        log.error('run button click error: ', error);
        displayGlobalMessage("Some error occurred!", 'error');
        setCustomEvaluationMessage("Error in starting evaluation...");

        // re-enable all coding actions
        setDisableCodingActions(false);


        setIsFetchingVisible(false);
        setIsFetchingHidden(false);

        log.error("run ", "failed. error: ", error);
        // If there is an error in the request, stop the interval and reset the flags and states
        log.debug('evaluate flag value in catch block: ', evaluate_flag);
        // if (evaluate_flag === "Evaluation Finished") {
        //   console.log("evaluation finished")
        //   clearInterval(interval);

        //   evaluate_flag = "Evaluation in Process"; // Resetting it for the upcoming click on the Run Button i.e. fresh request
        //   setEvaluationStatus(null); // Resetting it for the upcoming click on the Run Button i.e. fresh request
        //   if (testCaseType === "hidden") {
        //     setObtainedHiddenMarks(0);
        //   }
        //   if (testCaseType === "visible") {
        //     setObtainedVisibleMarks(0);
        //   }
        // }
      });

  }

  /**
   * Function to check the execution status of the submission, this will keep on polling the execution status until it is ready and once it is ready it will fetch the results and update the UI accordingly
   * @param {string} backgroundTaskId celery task id or 'DONE'
   * @param {number} submissionId current code submission ID
   * @param {boolean} isTestcaseVisible testcase visible or hidden
   * @param {number} maxRetries max number of polling retries
   * @param {number} baseDelay base polling delay
   * @param {number} maxDelay max polling delay
   * @returns None
   * Author: Aditya Upadhye
   */
  const checkExecutionStatus = async (
    backgroundTaskId,
    submissionId,
    isTestcaseVisible,
    maxRetries = 20,
    baseDelay = 200,
    maxDelay = 4000
  ) => {

    const url =
      constants.base_url +
      "/evalpro/evaluate/" +
      backgroundTaskId +
      "/status/";

    const params = {
      submission_id: submissionId,
      visible: isTestcaseVisible,
    };

    const sleep = (ms) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    const getBackoffDelay = (attempt) => {
      const exponential = baseDelay * (2 ** attempt); // base x 2^attempt, as attemp starts from 0
      return Math.min(exponential, maxDelay); // cap the delay to maxDelay
      // return exponential;

      // const jitter = Math.random() * 300; // prevent thundering herd
      // return exponential + jitter;
    };

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await httpService.get(url, { params });

        log.debug("response for execution status: ", response);

        const isTaskReady = response?.data?.ready;

        if (isTaskReady) {
          // ✅ Task finished — update results here

          // re-enable all coding actions
          setDisableCodingActions(false);

          // check for compilation errors
          if (response.data?.error == true) {
            if (response.data?.custom_status === "COMP_ERR") {
              setCustomEvaluationMessage("Compilation Error");
              displayGlobalMessage("Compilation Error", "error");

              setCompilerErrors(response.data?.result?.[0]?.compiler_errors);
              return;
            }
          }

          // Scoped to the polled set; the submission-level status is avoided as it reports a crash in either set.
          const runtimeError = buildRuntimeError(
            response.data?.custom_status,
            response.data?.result?.find((row) => row.error_messages)?.error_messages,
            response.data?.result
          );

          if (isTestcaseVisible) {
            setVisibleRuntimeError(runtimeError);
          } else {
            setHiddenRuntimeError(runtimeError);
          }

          if (runtimeError) {
            setCustomEvaluationMessage(runtimeError.label);
            displayGlobalMessage(runtimeError.label, "error");
            // Fall through so the per-testcase rows below still render.
          }

          let totalObtainedMarks = 0;
          for (const testCaseResult of response.data.result ?? []) {
            totalObtainedMarks += testCaseResult.marks_obtained;
          }

          if (isTestcaseVisible) {
            setObtainedVisibleMarks(totalObtainedMarks);
          }
          else {
            setObtainedHiddenMarks(totalObtainedMarks);
          }

          // common for both visible and hidden test cases
          setTestResults(response.data.result);
          // pass on newly selected submission
          setSelectedSubmission(selectedPopUpSubmission);

          // set loading to false for both
          setIsFetchingVisible(false);
          setIsFetchingHidden(false);

          return response.data;
        }

        // let customStatus = response.data['custom_status']['status'];
        let customStatus = response.data?.custom_status;

        if (response.data?.status === 'PENDING') {
          customStatus = "Submission waiting in the queue..";
        }

        setCustomEvaluationMessage(customStatus);

        // ⏳ Task not ready — retry if attempts left
        if (attempt === maxRetries) {
          // re-enable all coding actions
          setDisableCodingActions(false);

          // A run still pending here has outlived the sandbox clock limit, so name that rather than the retry count.
          const stillRunningError = {
            label: "Time Limit Exceeded",
            text: "Your program is still running after the allowed time. It was most likely stuck in an infinite loop or waiting for input that was never provided.",
          };
          if (isTestcaseVisible) {
            setVisibleRuntimeError(stillRunningError);
          } else {
            setHiddenRuntimeError(stillRunningError);
          }
          setCustomEvaluationMessage(stillRunningError.label);

          throw new Error(stillRunningError.text);
        }

        const delay = getBackoffDelay(attempt);
        log.debug(`Retrying in ${Math.round(delay)} ms...`);
        await sleep(delay);

      } catch (error) {
        log.error("check execution status error: ", error);

        // re-enable all coding actions
        setDisableCodingActions(false);

        setIsFetchingVisible(false);
        setIsFetchingHidden(false);

        displayGlobalMessage(error.message, 'error');

        // break loop on error
        break;

        // throw error; // stop retrying on network / server error
      }
    }

    displayGlobalMessage("Server Taking too long to respond!");
  };

  // function for fetching previous all submissions

  const fetchPreviousSubmissions = (e) => {
    setShowPopupSubmissions(true);
    var url = constants.base_url + "/evalpro/my_submissions/" + programId;
    var data = {};
    if (delegate_student_id) {
      data = { ...data, student_id: delegate_student_id };
    }
    httpService
      .get(url, {
        params: data,
      })
      .then((response) => {
        var count = Math.ceil(response.data.submissions.count / sizePage);
        setSubmissions(response.data.submissions.results);
        setPageCount(count);
      })
      .catch((error) => { });
  };

  const paginationHandler = (data) => {
    let currPageNum = data.selected + 1;

    var url =
      constants.base_url +
      "/evalpro/my_submissions/" +
      programId +
      "?page=" +
      currPageNum;
    var data = {};
    if (delegate_student_id) {
      data = { ...data, student_id: delegate_student_id };
    }
    httpService
      .get(url, {
        params: data,
      })
      .then((response) => {
        var count = Math.ceil(response.data.submissions.count / sizePage);
        setSubmissions(response.data.submissions.results);
        setPageCount(count);
      })
      .catch((error) => {
        log.error(error);
      });
  };

  // Function to handle file upload
  const onFileUpload = (e) => {
    e.preventDefault();

    // Get the selected files
    const files = selectedFiles;
    log.log("selected files", selectedFiles);

    // If no files are selected, display an error message and return
    if (files.length === 0) {
      displayGlobalMessage("No files selected", "error");
      setUploading(false);
      return;
    }

    const form_data = new FormData();

    // Iterate over the selected files
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Set the initial states for file size, upload progress, and upload response
      setFileSize(true);
      setFileUploadProgress(true);
      setFileUploadResponse(null);

      // If the file is empty, ask the user if they want to continue
      if (file.size === 0) {
        const flag = window.confirm(
          "The uploaded file is empty. Do you still want to continue?"
        );
        if (!flag) {
          setUploading(false);
          return;
        }
      }

      // If the file size is greater than 20480 bytes, set the states and return
      if (file.size > 20480) {
        setFileSize(false);
        setFileUploadProgress(false);
        setFileUploadResponse(null);
        setUploading(false);
        return;
      }

      // Append the file and its name to the form data
      form_data.append("file_submitted", file);
      form_data.append("names_of_files_submitted", file.name);
      if (delegate_student_id) {
        form_data.append("student_id", delegate_student_id);
      }
      if (isTemplateCode) {
        form_data.append("buffer_data", JSON.stringify(editorText));
        form_data.append("session_key", sessionKey);
      }
    }
    const url = constants.base_url + "/evalpro/upload/" + programId;
    httpService
      .post(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        // On successful upload, update the access control variables,
        // selected submission, and other states
        changeAccess(response.data.access_control_vars);
        dispatch(updateSelectedSubmission(response.data.student_submission.id));

        setFileUploadResponse("Upload Successful");
        setFileWasUploaded(true);
        setSelectedPopUpSubmission(response.data.student_submission);
        setObtainedHiddenMarks(null);
        setObtainedVisibleMarks(null);
        setTestResults(null);

        // const url =
        //     constants.base_url +
        //     response.data.student_submission.file_submitted
        //     .toString()
        //     .substr(1);
        // axios
        //     .get(url, {
        //         headers: {
        //             "Content-Type": "application/pdf",
        //         },
        //     })
        //     .then((res) => {
        //         // UpdateEditorState(res.data);
        //     });
        if (isTemplateCode) {
          setShowFinalCompilableCode(false);
          setShowConfirmSubmissionModal(false);
        }
        displayGlobalMessage("Submission uploaded Successfully", "success");
        setToggle(!toggle);
        setSelectedFiles([]);
      })
      .catch((error) => {
        // On error, set the upload response state to the error message
        log.log("Submission error:", error?.response?.status, error?.response?.data);
        if (error?.response?.status === 403) {
          const errorCode = error?.response?.data?.error_code;
          log.log("Error code received:", errorCode);
          if (errorCode === 'TIME_EXPIRED') {
            // Show the modal for time expired
            setShowTimeExpiredModal(true);
          } else if (errorCode === 'MAX_SUBMISSIONS') {
            // Show the modal for max submission attempts
            setShowMaxAttemptsModal(true);
          } else if (errorCode === 'IP_BLOCKED') {
            // Show the modal for IP blocked
            setShowIPBlockedModal(true);
          } else {
            // Default to max attempts modal for backward compatibility
            log.log("Unknown 403 error, defaulting to max attempts modal. Response data:", error?.response?.data);
            setShowMaxAttemptsModal(true);
          }
          return;
        }
        setFileUploadResponse(error.response.data.detail);
        if (isTemplateCode && error?.response?.status === 400 || isTemplateCode && error?.response?.status === 405) {
          setShowConfirmSubmissionModal(true);
        }
      })
      .finally(() => {
        log.debug("ManageSubmissions.jsx: Finalizing file upload process. Resetting states.");
        setUploading(false);
      });

    // Reset the upload progress and compiler/runtime errors states
    setFileUploadProgress(false);
    setCompilerErrors(null);
    setVisibleRuntimeError(null);
    setHiddenRuntimeError(null);
  };

  //Function to handle run without submit
  const onRunCode = async (e) => {
    try {
      setIsRunLoading(true);
      await handleDiscardChanges();
      setRunToggle(!runToggle);
      const url =
        constants.base_url +
        "/evalpro/editor/check_compilation_status/" +
        programId;
      const form_data = new FormData();
      form_data.append("programming_language", language);
      if (delegate_student_id) {
        form_data.append("student_id", delegate_student_id);
      }
      form_data.append("key", "buffer_data");
      const response = await httpService
        .put(url, form_data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })

      if (response?.data?.result?.status === "fail") {
        setErrorOnRun(response.data.result.error);
        setSuccessOnRun(false);
      } else {
        setErrorOnRun("");
        setSuccessOnRun(true);
      }
      setIsRunLoading(false);

    } catch (error) {
      log.error(error);
    } finally {
      setIsRunLoading(false);
    }
  };

  //Function to handle submit Button in the editor
  const onSubmitCode = (e) => {
    setUploading(true);
    const files = Object.keys(editorText).map((fileName) => {
      const content = editorText[fileName];
      const myBlob = new Blob([content], { type: "text/plain" });
      let fileNameWithExt = fileName.endsWith(".h")
        ? fileName
        : `${fileName}.${languageToFileExtension[language]}`;
      return new File([myBlob], fileNameWithExt, { type: myBlob.type });
    });
    setSelectedFiles(files);
    selectedFiles = files;
    onFileUpload(e);
  };

  // Function to handle final compilable code for template code
  const handleFinalCompilableCode = () => {

    // Get the selected files
    let files;
    if (selectedFiles.length !== 0) {
      files = selectedFiles;
    } else {
      files = Object.keys(editorText).map((fileName) => {
        const content = editorText[fileName];
        const myBlob = new Blob([content], { type: "text/plain" });
        let fileNameWithExt = fileName.endsWith(".h")
          ? fileName
          : `${fileName}.${languageToFileExtension[language]}`;
        return new File([myBlob], fileNameWithExt, { type: myBlob.type });
      });
      setSelectedFiles(files);
      selectedFiles = files;
    }

    // If no files are selected, display an error message and return
    if (files.length === 0) {
      displayGlobalMessage("No files selected", "error");
      return;
    }

    const form_data = new FormData();

    // Iterate over the selected files
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Set the initial states for file size, upload progress, and upload response
      setFileSize(true);
      setFileUploadProgress(true);
      setFileUploadResponse(null);

      // If the file is empty, ask the user if they want to continue
      if (file.size === 0) {
        const flag = window.confirm(
          "The uploaded file is empty. Do you still want to continue?"
        );
        if (!flag) {
          return;
        }
      }

      // If the file size is greater than 20480 bytes, set the states and return
      if (file.size > 20480) {
        setFileSize(false);
        setFileUploadProgress(false);
        setFileUploadResponse(null);
        return;
      }
      form_data.append("file_submitted", file);
      form_data.append("names_of_files_submitted", file.name);
      // form_data.append("buffer_data", JSON.stringify(editorText));
      // form_data.append("session_key", sessionKey);
    }
    const url = constants.base_url + `/evalpro/submission/generate_compilable_code/${programId}`;
    httpService
      .post(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        // handle success
        setFinalCompilableCode(response?.data?.compilable_code);
        setShowFinalCompilableCode(true);
        setShowConfirmSubmissionModal(false);
      })
      .catch((error) => {
        if (error?.response?.status === 404 || error?.response?.status === 400) {
          setShowFailedFinalCompilableCode(true);
          setShowFinalCompilableCode(false);
          setShowConfirmSubmissionModal(false);
        }
      });
    setShowConfirmSubmissionModal(false)
    return 0;
  }

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor; // Store the editor instance in the ref
    setMonacoInstance(monaco);

    const disposable = editor?.onDidChangeModelContent((event) => {
      if (event.changes.length > 0) {
        latestChangeRef.current = event.changes[event.changes.length - 1];
        setChange(event.changes);
      }
    });

    // Store the disposable listener for later removal
    editorRef.current._changeListener = disposable;

    // === Cursor position listener ===
    const cursorDisposable = editor.onDidChangeCursorPosition((e) => {
      const line = e.position.lineNumber;
      if (e?.position) {
        setCursorPosition(e?.position);
      }
    });

    // Store cursor listener for later cleanup
    editorRef.current._cursorListener = cursorDisposable;
    editor.addAction({
      id: "ask-bodhibot",
      label: "Ask Bodhibot",
      contextMenuGroupId: "navigation",
      contextMenuOrder: 1,
      run: function (editor) {
        const selectedText = editor.getModel().getValueInRange(editor.getSelection());
        if (selectedText.trim()) {
          const data = {
            type: "codeSnippet",
            content: selectedText,
            source: "monaco-editor",
            language: editor.getModel()?.getLanguageId(),
          };
          dispatch(setChatbotExternalData(data));
        }
      },
    });
  };

  // This function is used to set the selected submission from a list of submissions.
  const SetSelectedSubmission = (event) => {
    // Get all elements with the name "submissions"
    var ele = document.getElementsByName("submissions");
    // Initialize submission id and checked element value
    var sub_id = -1;
    var checked_ele_value = -1;
    // Loop through all elements to find the checked one
    for (let i = 0; i < ele.length; i++) {
      // If the element is checked, get its value and the corresponding submission id
      if (ele[i].checked) {
        checked_ele_value = ele[i].value;
        sub_id = submissions[checked_ele_value]["id"];
        break;
      }
    }

    let form_data = new FormData();
    if (delegate_student_id) {
      form_data.append("student_id", delegate_student_id);
    }
    let url =
      constants.base_url +
      "/evalpro/select_submission_for_evaluation/" +
      sub_id;

    // Make a PUT request to the server
    httpService
      .put(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        // This code will execute after the PUT request is successful
        // Update the selected submission in the state
        dispatch(updateSelectedSubmission(submissions[checked_ele_value].id));
        setSelectedPopUpSubmission(submissions[checked_ele_value]);
        setSelectedSubmission(submissions[checked_ele_value]);
        setShowPopupSubmissions(false);

        // Check if execution is pending for visible and hidden test cases, and set marks accordingly
        if (
          !submissions[checked_ele_value]
            .execution_pending_for_visible_testcases
        ) {
          setObtainedVisibleMarks(
            submissions[checked_ele_value].obtained_visible_marks
          );
        } else {
          setObtainedVisibleMarks(null);
        }

        if (
          !submissions[checked_ele_value].execution_pending_for_hidden_testcases
        ) {
          setObtainedHiddenMarks(
            submissions[checked_ele_value].obtained_hidden_marks
          );
        } else {
          setObtainedHiddenMarks(null);
        }

        // Reset custom result
        setCustomResult(null);

        // Construct the URL for the GET request
        var latest_url = constants.base_url + "/evalpro/get_latest/" + programId;

        // Make a GET request to the server
        httpService
          .get(latest_url)
          .then((response) => {
            // Update access control variables and compiler errors in the state
            changeAccess(response.data.access_control_vars);
            setCompilerErrors(response.data.student_submission.compiler_errors);
          })
          .catch((error) => {
            // Log any errors from the GET request
            log.error(error);
          });
      })
      .catch((error) => {
        // Handle any errors from the PUT request
      });
  };

  // This function is triggered when files are dropped into the dropzone.
  const onDrop = (acceptedFiles) => {
    // Filter out the files that are already selected.
    // This prevents duplicate files from being selected.
    const newFiles = acceptedFiles.filter(
      (file) =>
        // The 'some' method tests whether at least one element in the array passes the test implemented by the provided function.
        // In this case, it checks if the file is not already in the selectedFiles array by comparing their names.
        !selectedFiles.some((selectedFile) => selectedFile.name === file.name)
    );
    // Update the selectedFiles state with the new files.
    // The spread operator (...) is used to create a new array that contains the values of the old array (selectedFiles) and the new array (newFiles).
    setSelectedFiles([...selectedFiles, ...newFiles]);
  };

  // Function to remove a file from the selected files
  const removeFile = (fileIndex) => {
    // Create a new array from the current selected files
    const newSelectedFiles = [...selectedFiles];
    // Remove the file at the specified index
    newSelectedFiles.splice(fileIndex, 1);
    // Update the state with the new array of selected files
    setSelectedFiles(newSelectedFiles);
  };

  // Function to handle changes in the editor
  const handleEditorChange = (value) => {
    // If ignoreChange is true, reset it to false and return immediately. This is to prevent an infinite loop when setting the editor value.
    if (ignoreChange.current) {
      ignoreChange.current = false;
      return;
    }
    // If the file is an instructor-provided file, display a message and prevent editing
    if (isInstructorFile) {
      displayGlobalMessage(
        "You are viewing an instructor-provided file. Editing is not allowed.",
        "info"
      );
      //If the editor reference is valid, reset the editor content to the original content
      if (editorRef.current) {
        ignoreChange.current = true;
        editorRef.current.setValue(
          instructorAdditionalFilesContent[language][currentFileinEditor]
        );
        const decorations = [];
        decorations.push({
          range: new monacoInstance.Range(0, 1, instructorFileLastLineNumber, 1),
          options: {
            isWholeLine: true,
            className: "custom-region-read_only",
          },
        })
        //applying decorations to instructor additional file
        editorRef.current.deltaDecorations([], decorations);
      }
      return;
    }
    // If the file is not an instructor-provided file, update the editor text state with the new value
    const updatedEditorText = { ...editorText };
    updatedEditorText[currentFileinEditor] = value;
    setEditorText(updatedEditorText);
  };


  return (
    <div>
      {/* Heading for the file upload option */}
      <h6 style={{ marginTop: "5px" }}>
        <u>Option 1: Upload Your Code File</u>
      </h6>

      {/* Display the allowed submission file names */}
      <div style={{ padding: "5px 0px" }}>
        Allowed Submission File Name(s):{" "}
        <b>
          <i>{Expected_Submission_File}</i>
        </b>
      </div>

      {/* Container for the file upload and upload Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingRight: "24px",
        }}
      >
        <div>
          {/* If file upload is enabled, display the Dropzone component for file upload */}
          {access.enable_upload_submission ? (
            <Dropzone
              onDrop={onDrop}
              accept={{
                'text/x-csrc': ['.c'],
                'text/x-c++src': ['.cpp'],
                'text/x-java': ['.java'],
                'text/x-python': ['.py']
              }}
            >
              {({ getRootProps, getInputProps }) => (
                <section className="wfp--dropzone">
                  <div {...getRootProps({ className: "wfp--dropzone__input" })}>
                    <input {...getInputProps()} />
                    <div>
                      <Button variant="secondary">Choose File</Button>
                    </div>
                  </div>

                  {/* List of selected files */}
                  <ul style={{ paddingInlineStart: "0" }}>
                    {selectedFiles?.map((file, index) => (
                      <li
                        key={index}
                        style={{
                          marginBottom: "4px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "2px",
                          background: "#edf0f2",
                          borderRadius: "4px",
                        }}
                      >
                        {/* Display the file name and a Button to remove the file */}
                        <span>{file?.name}</span>{" "}
                        <Button
                          variant="danger"
                          onClick={() => removeFile(index)}
                          style={{ padding: "0px 6px", borderRadius: "4px" }}
                        >
                          x
                        </Button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </Dropzone>
          ) : (
            /* If file upload is not enabled, display a disabled Button with a tooltip */
            <OverlayTrigger
              id="ChooseFile"
              overlay={<Tooltip>Deadline passed</Tooltip>}
            >
              <Button variant="secondary" disabled>
                Choose File
              </Button>
            </OverlayTrigger>
          )}
        </div>

        {/* Upload Button */}
        <div>
          {access.enable_upload_submission ? (
            <Button variant="primary" onClick={onFileUpload} id={1}>
              Upload
            </Button>
          ) : (
            <Button style={{ marginBottom: "16px" }} disabled>
              Upload
            </Button>
          )}
        </div>
      </div>

      {/* Alert for file size exceeded */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Alert
          show={!fileSize}
          variant="danger"
          style={{ marginBottom: "10px" }}
          onClose={() => setFileSize(true)}
          dismissible
        >
          File size exceeded!!
        </Alert>
      </div>

      {/* Alert for file upload response */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {fileUploadResponse !== null && (
          <Alert
            show={true}
            variant={
              fileUploadResponse === "Upload Successful" ? "success" : "danger"
            }
            style={{ marginBottom: "10px" }}
            onClose={() => setFileUploadResponse(null)}
            dismissible
          >
            {fileUploadResponse}
          </Alert>
        )}
      </div>

      {/* Horizontal line */}
      <hr style={{ marginTop: "0" }} />

      {/* Integrated Code editor start*/}
      {/* Heading for the code writing and submission option */}
      <h6>
        <u>Option 2: Write and Submit Your Code</u>
      </h6>

      {/* Container for the integrated code editor */}
      <div
        id="Integrated code editor"
        style={{
          border: "3px solid #b5b3b3", // Border styling
          borderRadius: "5px", // Rounded corners
          padding: "8px 8px 0px 8px", // Padding
        }}
      >
        {/* Modal for handling buffer changes */}
        <Modal
          dialogClassName="modal-70w" // Custom class for modal
          show={showEditorPopup} // Show modal when buffer changes
          backdrop="static" // Disables closing the modal by clicking outside of it
          keyboard={false} // Disables closing the modal with the escape key
          centered // Centers the modal vertically in the viewport
        >
          <Modal.Header>
            {/* Modal title */}
            <Modal.Title>Buffer Changed!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Modal body text */}
            Code in buffer has changed. Do you want to load changes from the
            buffer or keep your current code?
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            {/* Button to load changes from the buffer */}
            <Button variant="primary" onClick={handleLoadChanges}>
              Load Changes
            </Button>
            {/* Button to keep the current code */}
            <Button variant="secondary" onClick={handleDiscardChanges}>
              Keep Current Code
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal for handling submission confirmation for template coding */}
        <Modal
          dialogClassName="modal-70w"
          show={isTemplateCode && showConfirmSubmissionModal}
          backdrop="static"
          top
        >
          <Modal.Header>
            <Modal.Title>Confirm Submission?</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            It seems you have modified instructor uploaded driver code. Do you want to replace the modified driver code with the original instructor uploaded driver code?
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-between">
            <Button variant="primary" onClick={() => {
              handleFinalCompilableCode()
            }}>
              Show final compilable code
            </Button>
            <Button variant="primary" onClick={() => { setShowConfirmSubmissionModal(false) }}>
              No, don't submit my code
            </Button>
          </Modal.Footer>

        </Modal>

        {/* Modal for displaying final compilable code while making submission for template coding */}
        <Modal
          dialogClassName="final_compilable_code"
          show={isTemplateCode && showFinalCompilableCode}
          backdrop="static"
          centered
        >
          <Modal.Header className="d-flex justify-content-between align-items-center">
            <Modal.Title>Final Compilable Code</Modal.Title>
            <div className="d-flex ms-auto gap-2">
              <Button
                variant="primary"
                onClick={(e) => {
                  //let tempKey = Object.keys(finalCompilableCode)[0];
                  let tempEditorText = {};
                  Object.keys(finalCompilableCode)?.forEach((key) => {
                    let tempKey = key.split(".")[0];
                    tempEditorText[tempKey] = finalCompilableCode[key];
                  })
                  setEditorText(tempEditorText);
                  setShowFinalCompilableCode(false);
                  setShowConfirmSubmissionModal(false);
                }}
              >
                Load on Editor
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setShowFinalCompilableCode(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </Modal.Header>

          <Modal.Body>
            {finalCompilableCode && Object.keys(finalCompilableCode)?.length > 0 ? (
              <Tabs activeKey={Object.keys(finalCompilableCode)?.[0]} onSelect={(k) => setActiveTab(k)} className="mb-3">
                {Object.entries(finalCompilableCode)?.map(([fileName, fileContent]) => (
                  <Tab eventKey={fileName} title={fileName} key={fileName}>
                    <div style={{ position: "relative" }}>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        style={{
                          position: "absolute",
                          border: "1px solid grey",
                          top: "10px",
                          right: "10px",
                          zIndex: 1,
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(fileContent).then(() => {
                            alert("Code copied to clipboard!");
                          }).catch((err) => {
                            log.error("Failed to copy: ", err);
                          });
                        }}
                      >
                        📋 Copy
                      </Button>

                      <pre
                        style={{
                          backgroundColor: "#f5f5f5",
                          padding: "10px",
                          borderRadius: "5px",
                          whiteSpace: "pre-wrap",
                          overflowX: "auto",
                        }}
                      >
                        <code>{fileContent}</code>
                      </pre>
                    </div>
                  </Tab>
                ))}
              </Tabs>
            ) : (
              <p>No files available</p>
            )}
          </Modal.Body>

        </Modal>
        <Modal show={showMaxAttemptsModal} centered>
          <Modal.Header closeButton={false}>
            <Modal.Title>Maximum Submissions Reached</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            You have reached the maximum number of submission attempts.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMaxAttemptsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showTimeExpiredModal} centered>
          <Modal.Header closeButton={false}>
            <Modal.Title>Lab Time Expired</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            The lab time has expired. You can no longer submit your code.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTimeExpiredModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showIPBlockedModal} centered backdrop="static" keyboard={false}>
          <Modal.Header>
            <Modal.Title>Exam Access Blocked</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            You have been blocked from this exam. Please contact your instructor.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => {
              window.location.href = window.location.href.split('/content/labs/')[0] + '/content/labs';
            }}>
              Go to Labs
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          dialogClassName="modal-70w"
          show={isTemplateCode && showFailedFinalCompilableCode}
          top
        >
          <Modal.Header>
            <Modal.Title>Cannot generate compilable code</Modal.Title>
            <Button
              variant="danger"
              onClick={() => {
                setShowFailedFinalCompilableCode(false);
              }}
            >
              Close
            </Button>
          </Modal.Header>

          <Modal.Body>
            Sorry, we were unable to generate the final compilable code as you have modified some student slice markers.
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">

          </Modal.Footer>

        </Modal>


        {/* FullScreen component for the code editor */}
        <FullScreen
          handle={handle}
          onChange={(state) => setisFullScreen(state)}
          style={{ backgroundColor: "white" }}
        >
          <div style={{ backgroundColor: "white" }}>
            <div
              className="editor-toolbar"
              style={{
                padding: isFullScreen ? "8px" : "0px 0px 8px 0px",
              }}
            >
              <ButtonGroup className="editor-toolbar-file">
                {allFiles.map((key, index) => {
                  const isUserFile =
                    submission.files_to_be_submitted.includes(key);
                  const isHeaderFileToBeSubmitted =
                    headerFileToBeSubmitted.includes(key);
                  const fileName = isUserFile
                    ? `${key}.${languageToFileExtension[language]}`
                    : key;
                  const isSelected = key === currentFileinEditor;

                  return (
                    <Button
                      key={index}
                      variant={
                        isSelected
                          ? isUserFile || isHeaderFileToBeSubmitted
                            ? "secondary"
                            : "info"
                          : isUserFile || isHeaderFileToBeSubmitted
                            ? "outline-secondary"
                            : "outline-info"
                      } // Use 'secondary' if selected, otherwise 'outline-secondary'
                      size="sm"
                      onClick={() => {
                        setPrevFileInEditor(currentFileinEditor)
                        setCurrentFileinEditor(key);
                        setMarkersMapping([]);
                        setIsInstructorFile(
                          !isUserFile && !isHeaderFileToBeSubmitted
                        );
                      }}
                    >
                      {fileName}
                    </Button>
                  );
                })}
              </ButtonGroup>
              <div className="editor-toolbar-actions">
                  {isTemplateCode ? (
                    <>
                      <OverlayTrigger overlay={<Tooltip>Reset Code</Tooltip>}>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            if (window.confirm("Are you sure you want to reset your code to initial instructor code?")) {
                              const updatedEditorText = {};
                              Object.keys(initialCode)?.forEach((key) => {
                                let tempKey = key.split(".")[0];
                                updatedEditorText[tempKey] = initialCode[key];
                              });
                              setEditorText(updatedEditorText);
                            }
                          }}
                        >
                          <CgRedo />
                        </Button>
                      </OverlayTrigger>
                      {' '}
                      <OverlayTrigger overlay={<Tooltip>Download</Tooltip>}>
                        <Button
                          variant="secondary"
                          onClick={() => downloadHandler()}
                        >
                          <IoMdDownload />
                        </Button>
                      </OverlayTrigger>
                      {' '}
                    </>
                  ) : (
                    <>
                      <OverlayTrigger overlay={<Tooltip>Change Theme</Tooltip>}>
                        <Button
                          variant="secondary"
                          onClick={() => modeHandler()}
                        >
                          {mode === 'light' ? <MdLightMode /> : <MdDarkMode />}
                        </Button>
                      </OverlayTrigger>
                      {' '}
                    </>
                  )}

                  <OverlayTrigger
                    overlay={<Tooltip>Toggle Fullscreen</Tooltip>}
                  >
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (isFullScreen) {
                          handle.exit();
                        } else {
                          handle.enter();
                        }
                      }}
                    >
                      {isFullScreen ? (
                        <MdOutlineFullscreenExit />
                      ) : (
                        <MdOutlineFullscreen />
                      )}
                    </Button>
                  </OverlayTrigger>{" "}
                  <OverlayTrigger overlay={<Tooltip>Zoom In</Tooltip>}>
                    <Button
                      variant="secondary"
                      onClick={() => setFontSize(fontSize + 1)}
                    >
                      <MdOutlineZoomIn />
                    </Button>
                  </OverlayTrigger>{" "}
                  <OverlayTrigger overlay={<Tooltip>Zoom Out</Tooltip>}>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (fontSize > 1) setFontSize(fontSize - 1);
                      }}
                    >
                      <MdOutlineZoomOut />
                    </Button>
                  </OverlayTrigger>{" "}
                </div>
                <Dropdown className="editor-toolbar-lang">
                  <Dropdown.Toggle variant="secondary" size="sm">
                    {`Language: ${language}`}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {submission?.program_language.map((lang, index) => (
                      <Dropdown.Item
                        key={index}
                        onClick={() => {
                          setLanguage(lang);
                          const userFiles =
                            submission.files_to_be_submitted.filter(
                              (fileName) =>
                                !instructorAdditionalFilesContent[
                                  lang
                                ]?.hasOwnProperty(fileName)
                            );
                          setPrevFileInEditor(currentFileinEditor)
                          setCurrentFileinEditor(userFiles[0]);
                          setIsInstructorFile(false);
                          // Set the first user file when language changes
                        }}
                      >
                        {lang}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
            </div>

            {language === "Java" && submission.files_to_be_submitted?.length > 0 && (
              <div className="text-muted" style={{ fontSize: "0.85rem", marginBottom: "8px" }}>
                {`Java: declare your main class as "public class ${submission.files_to_be_submitted[0]}" in ${submission.files_to_be_submitted[0]}.java`}
              </div>
            )}

            <div
              style={{
                border: "2px solid #ccc",
                borderRadius: "5px",
                padding: "8px",
                marginBottom: "8px",
              }}
            >
              <MonacoEditorComponent
                height={isFullScreen ? "95vh" : "50vh"}
                width="100%"
                language={languageToSyntaxHighlighting[language]}
                path={currentFileinEditor}
                value={
                  isInstructorFile
                    ? instructorAdditionalFilesContent[language][
                    currentFileinEditor
                    ]
                    : editorText[currentFileinEditor]
                }
                onChange={handleEditorChange}
                options={{
                  automaticLayout: true,
                  fontSize: fontSize,
                  minimap: {
                    enabled: true,
                    scale: 3,
                    size: 'proportional',
                    showSlider: 'always',
                    renderCharacters: true,
                    maxColumn: 80,
                  },
                }}
                onMount={handleEditorDidMount}
                theme={mode === 'light' ? 'light' : 'vs-dark'}
              />
            </div>
          </div>
        </FullScreen>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingRight: "1rem",
            paddingBottom: "-16px",
            height: "50px",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                gap: "10px",
                alignItems: "flex-end",
              }}
            >
              <OverlayTrigger
                id="enabledPreviousVersion"
                overlay={
                  <Tooltip>
                    {access.enable_select_previous_submission
                      ? "By default the latest submitted code is taken as final submission. Click here to change."
                      : "Disabled because  deadline passed or no submission"}
                  </Tooltip>
                }
              >
                {access.enable_select_previous_submission ? (
                  <Button
                    variant="secondary"
                    onClick={(e) => fetchPreviousSubmissions(e)}
                  >
                    <FaEdit size="20" /> Previous Submissions
                  </Button>
                ) : (
                  <Button variant="secondary" disabled>
                    <FaEdit size="20" /> Previous Submissions
                  </Button>
                )}
              </OverlayTrigger>
              <p
                style={{ marginBottom: "0px", color: "#666", fontSize: "12px" }}
              >
                <i>
                  {lastSavedTime
                    ? "AutoSaved: " + lastSavedTime.toLocaleString()
                    : ""}
                </i>
              </p>
            </div>
            <div
              className="col-12"
              style={{ marginLeft: "250px", marginTop: "20px" }}
            >
              <Modal
                show={showPopupSubmissions}
                dialogClassName="modal-90w"
                onHide={() => {
                  setShowPopupSubmissions(false);
                }}
                size="xl"
                aria-labelledby="contained-modal-title-vcenter"
                centered
              >
                <Modal.Header closeButton>
                  <div style={{ color: "red", fontSize: "15px" }}>
                    Text in red :{" "}
                  </div>
                  <div style={{ paddingLeft: "10px" }}>
                    Indicates current submission
                  </div>
                  <Button
                    style={{ marginLeft: "300px" }}
                    onClick={(e) => SetSelectedSubmission()}
                  >
                    Select
                  </Button>
                </Modal.Header>
                <Modal.Body>
                  <div className="row">
                    <div className="col-1"></div>
                    <div className="col-2">Files</div>
                    <div className="col-3">Submitted at</div>
                    <div className="col-3">Practice Marks</div>
                    <div className="col-3">Submitted by</div>
                  </div>
                  <hr />
                  {submissions?.map((item, i) => (
                    <div key={i}>
                      <div className="row">
                        <div className="col-1">
                          <input
                            type="radio"
                            name="submissions"
                            value={i}
                          ></input>
                        </div>

                        <div
                          className="col-2"
                          style={{
                            color:
                              selectedPopUpSubmission.id == item.id
                                ? "red"
                                : "inherit",
                          }}
                        >
                          {item.names_of_files_submitted.join(", ")}
                        </div>

                        <div className="col-3">
                          {" "}
                          {getFormattedDate(item.uploaded_on, true)}
                        </div>
                        {item.compiler_errors ? (
                          <div className="col-3">Compilation error</div>
                        ) : (
                          <div className="col-3">
                            {item.execution_pending_for_visible_testcases ? (
                              <div>Run to View Marks</div>
                            ) : (
                              <div className="col-3">
                                {item.obtained_visible_marks}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="col-3">
                          {item.submitted_by
                            ? item.submitted_by.first_name +
                            " " +
                            item.submitted_by.last_name +
                            " (" +
                            item.submitted_by.username +
                            ")"
                            : null}
                        </div>
                      </div>
                      <br />
                    </div>
                  ))}
                </Modal.Body>

                <ReactPaginate
                  previousLabel={"Prev"} //just a label in UI for prev Button
                  nextLabel={"Next"} //just a label in UI for next Button
                  breakLabel={"..."} //just a label in UI for breaking the pagination labels
                  pageCount={pageCount} //count for total number of pages over which all labs are spanning
                  marginPagesDisplayed={2} //if it is '2' i.e. in the begin and last it will show to 2 page numbers e.g. (1) (2) ... (n-1) (n)
                  pageRangeDisplayed={3} //total page numbers is to be shown in middle after break (i.e after clicking on 3 dots)
                  onPageChange={paginationHandler} //function i.e. making an axios call to fetch a particular page based on the clicked number
                  containerClassName={"pagination justify-content-center"} //css class for styling, similarly all below className(s) are also for styling
                  pageClassName={"page-item"}
                  pageLinkClassName={"page-link"}
                  previousClassName={"page-item"}
                  previousLinkClassName={"page-link"}
                  nextClassName={"page-item"}
                  nextLinkClassName={"page-link"}
                  breakClassName={"page-item"}
                  breakLinkClassName={"page-link"}
                  activeClassName={"active"}
                />
              </Modal>
            </div>
          </div>
          <div>
            {displayRunWithoutSubmitButton && (
              <OverlayTrigger overlay={<Tooltip>Run Without Submit</Tooltip>}>
                <Button
                  variant="primary"
                  onClick={onRunCode}
                  disabled={disableCodingActions || access.enable_run_without_submit === false}
                  style={{ marginRight: "1rem" }}
                >
                  <VscRunAll />
                </Button>
              </OverlayTrigger>
            )}
            <Button
              variant="primary"
              onClick={(e) => {
                onSubmitCode(e);
              }}
              disabled={uploading || disableCodingActions || access.enable_upload_submission === false}
            >
              {uploading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="mr-2"
                  />
                  Submitting Code...
                </>
              ) : (
                "Submit Code"
              )}
            </Button>
          </div>
        </div>
        <RunTerminal
          errorOnRun={errorOnRun}
          programId={programId}
          student_id={delegate_student_id}
          language={language}
          runToggle={runToggle}
          successOnRun={successOnRun}
          setRunToggle={setRunToggle}
          setErrorOnRun={setErrorOnRun}
          setSuccessOnRun={setSuccessOnRun}
          isRunLoading={isRunLoading}
          setToggle={setToggle}
          toggle={toggle}
        />
      </div>
      {/* Integrated Code editor end*/}

      {/* New Result Row */}
      {/* Create a table with 4 columns and 2 rows */}
      <div className="border p-2 text-center">
        <Table responsive>
          <thead>
            <tr>
              <th>File Submitted</th>
              <th>Time of Submission</th>
              <th>Practice Marks</th>
              <th>Evaluate Marks</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {/* File(s) submitted */}
                <div id="file-submitted">
                  <a href={getDownloadPath()} download>
                    {selectedPopUpSubmission?.names_of_files_submitted?.map(
                      (fileName, index) => (
                        <div key={index}>{fileName}</div>
                      )
                    )}
                  </a>
                </div>
              </td>
              <td>
                {/* Time of submission */}
                <div>{getSubmissionDate()}</div>
              </td>
              <td>
                {/* Practice marks */}
                <div id="practice-marks">
                  {access.enable_show_visible_marks === false ? (
                    <div>
                      {
                        access.enable_run_visible_testcases ? (
                          <div>
                            {compilerErrors ? (
                              <Button
                                variant="link"
                                onClick={() => setShowPopupCompilerError(true)}
                                style={{
                                  color: "red",
                                }}
                              >
                                <u>Compilation Error</u>
                              </Button>
                            ) : visibleRuntimeError ? (
                              <Button
                                variant="link"
                                onClick={() => {
                                  setActiveRuntimeError(visibleRuntimeError);
                                  setShowPopupRuntimeError(true);
                                }}
                                style={{
                                  color: "red",
                                }}
                              >
                                <u>{visibleRuntimeError.label}</u>
                              </Button>
                            ) : (
                              disableCodingActions && isFetchingVisible ? (
                                <Alert variant="info">
                                  <Spinner animation="border" size="sm" className="mr-3" />
                                  {customEvaluationMessage}
                                </Alert>
                              ) : (
                                <OverlayTrigger overlay={<Tooltip>Run</Tooltip>}>
                                  <Button
                                    variant="primary"
                                    id="run-practice-marks-button"
                                    onClick={(e) => runTestCases("visible")}
                                    disabled={disableCodingActions || access.enable_run_without_submit === false}
                                  >
                                    <AiOutlinePlayCircle />
                                  </Button>
                                </OverlayTrigger>
                              )
                            )}
                          </div>
                        ) : (
                          <div>
                            {
                              selectedPopUpSubmission == null ? (
                                <div>-</div>
                              ) : (
                                // placeholder button
                                <OverlayTrigger overlay={<Tooltip>Access Denied</Tooltip>}>
                                  <Button
                                    variant="outline-primary"
                                    id="run-practice-marks-button-placeholder"
                                    // onClick={(e) => runTestCases("visible")}
                                    disabled={true}
                                  >
                                    <AiOutlinePlayCircle />
                                  </Button>
                                </OverlayTrigger>
                              )
                            }
                          </div>
                        )
                      }
                    </div>
                  ) : (
                    <div>{obtainedVisibleMarks}</div>
                  )}
                </div>
              </td>
              <td>
                {/* Evaluate marks */}
                <div id="evaluate-marks">
                  {/* {access.enable_show_hidden_marks === false ? (
                    <div>
                      <div>
                        {access.enable_run_hidden_testcases &&
                          isFetchingHidden === false ? (
                          <div>
                            {compilerErrors ? (
                              <Button 
                                variant="link"
                                onClick={() => setShowPopupCompilerError(true)}
                                style={{
                                  color: "red",
                                }}
                              >
                                <u>Compilation Error</u>
                              </Button>
                            ) : (
                              <OverlayTrigger overlay={<Tooltip>Run</Tooltip>}>
                                <Button
                                  id="evaluate-marks-button"
                                  variant="primary"
                                  onClick={(e) => runTestCases("hidden")}
                                  disabled={disableCodingActions}
                                >
                                  <AiOutlinePlayCircle size="20" />
                                </Button>
                              </OverlayTrigger>
                            )}
                          </div>
                        ) : (
                          <OverlayTrigger
                            overlay={
                              <Tooltip>
                                {isFetchingHidden
                                  ? "Submission running on Evaluation testcases"
                                  : submission.hidden_testcases_count
                                    ? "You can run your submission on Evaluate testcases only after deadline has been passed"
                                    : "No evaluation testcases"}
                              </Tooltip>
                            }
                          >
                            <div>
                              <Button variant="outline-secondary" disabled>
                                <AiOutlinePlayCircle size="20" />
                              </Button>
                            </div>
                          </OverlayTrigger>
                        )}
                      </div>
                      <div style={{ marginLeft: "0px" }}>
                        {isFetchingHidden && (
                          <p style={{ color: "red" }}>
                            <AiOutlineRetweet size="20" />
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>{obtainedHiddenMarks}</div>
                  )} */}

                  {access.enable_show_hidden_marks === false ? (
                    <div>
                      {
                        access.enable_run_hidden_testcases ? (
                          <div>
                            {compilerErrors ? (
                              <Button
                                variant="link"
                                onClick={() => setShowPopupCompilerError(true)}
                                style={{
                                  color: "red",
                                }}
                              >
                                <u>Compilation Error</u>
                              </Button>
                            ) : hiddenRuntimeError ? (
                              <Button
                                variant="link"
                                onClick={() => {
                                  setActiveRuntimeError(hiddenRuntimeError);
                                  setShowPopupRuntimeError(true);
                                }}
                                style={{
                                  color: "red",
                                }}
                              >
                                <u>{hiddenRuntimeError.label}</u>
                              </Button>
                            ) : (
                              disableCodingActions && isFetchingHidden ? (
                                <Alert variant="info">
                                  <Spinner animation="border" size="sm" className="mr-3" />
                                  {customEvaluationMessage}
                                </Alert>
                              ) : (
                                <OverlayTrigger overlay={<Tooltip>Run</Tooltip>}>
                                  <Button
                                    variant="primary"
                                    id="run-hidden-marks-button"
                                    onClick={(e) => runTestCases("hidden")}
                                    disabled={disableCodingActions || access.enable_run_without_submit === false}
                                  >
                                    <AiOutlinePlayCircle />
                                  </Button>
                                </OverlayTrigger>
                              )
                            )}
                          </div>
                        ) : (
                          <div>
                            {
                              selectedPopUpSubmission == null ? (
                                <div>-</div>
                              ) : (
                                // placeholder button
                                <OverlayTrigger overlay={<Tooltip>Access Denied</Tooltip>}>
                                  <Button
                                    variant="outline-primary"
                                    id="run-evaluate-marks-button-placeholder"
                                    // onClick={(e) => runTestCases("visible")}
                                    disabled={true}
                                  >
                                    <AiOutlinePlayCircle />
                                  </Button>
                                </OverlayTrigger>
                              )
                            }
                          </div>
                        )
                      }
                    </div>
                  ) : (
                    <div>{obtainedHiddenMarks}</div>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </Table>
      </div>

      {/* Modal for displaying compiler errors */}
      <Modal
        show={showPopupCompilerError} // Show modal when there are compiler errors
        dialogClassName="modal-90w" // Custom class for modal
        onHide={() => setShowPopupCompilerError(false)} // Hide modal when the close Button is clicked
        size="xl" // Extra large modal
        aria-labelledby="contained-modal-title-vcenter" // Accessibility label
        centered // Centers the modal vertically in the viewport
      >
        <Modal.Header closeButton>Compilation Error</Modal.Header>{" "}
        {/* Modal title */}
        <Modal.Body>
          <div className="row">
            <div className="col-12 col1">
              {/* Textarea for displaying the compiler errors */}
              <textarea
                className="textarea1" // Custom class for textarea
                value={compilerErrors} // Display the compiler errors
                style={{
                  color: "red", // Red text
                  backgroundColor: "black", // Black background
                  width: "100%", // Full width
                  height: "200px", // Fixed height
                }}
                disabled // Disable user input
              ></textarea>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Modal for displaying runtime errors, mirroring the compilation error modal above */}
      <Modal
        show={showPopupRuntimeError}
        dialogClassName="modal-90w"
        onHide={() => setShowPopupRuntimeError(false)}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>{activeRuntimeError?.label || "Runtime Error"}</Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-12 col1">
              <p>
                Your program compiled, but did not run to completion on one or more test
                cases. No marks are awarded for a test case that fails to run, even if the
                output produced before the error was correct.
              </p>
              <textarea
                className="textarea1"
                value={activeRuntimeError?.text || ""}
                style={{
                  color: "red",
                  backgroundColor: "black",
                  width: "100%",
                  height: "200px",
                }}
                disabled
              ></textarea>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <Card>
        {/* Show the custom input card if the flag is set to true */}
        {showCustomInputCard === true ? (
          <Card.Body>
            {/* Header row for input and output */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <b>INPUT</b>
              </div>
              <div>
                <b>OUTPUT</b>
              </div>
              <div>
                <b></b>
              </div>
            </div>

            {/* Row for input and output textareas */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                {/* Textarea for entering custom input */}
                <textarea
                  className="textarea6"
                  style={{
                    backgroundColor: "black",
                    height: "100px",
                    width: "200px",
                  }}
                  placeholder="Enter your input"
                  onChange={(e) => setCustomInput(e.target.value)}
                ></textarea>
              </div>
              <div>
                {/* Textarea for displaying the output */}
                <textarea
                  className="textarea6"
                  style={{
                    backgroundColor: "black",
                    height: "100px",
                    width: "200px",
                  }}
                  value={customResult?.output || ""} // Display the output or an empty string
                  placeholder="output"
                  disabled // Disable user input
                ></textarea>
              </div>

              {/* Error message */}
              <div id="Custom input error">
                <OverlayTrigger overlay={<Tooltip>Click to Expand</Tooltip>}>
                  <a
                    onClick={() => setShowPopupCustomInput(true)}
                    style={{
                      color: "red",
                    }}
                  >
                    {/* return_code catches a crash that left no stderr behind */}
                    {customResult?.runtime_errors ||
                      customResult?.compilation_errors ||
                      customResult?.return_code ||
                      customResult?.verdict ? (
                      <u>{customResult?.verdict || "Error"}</u>
                    ) : (
                      ""
                    )}
                  </a>
                </OverlayTrigger>
              </div>
            </div>

            {/* Modal for displaying compiler and runtime errors */}
            <Modal
              show={showPopupCustomInput} // Show modal when the flag is set to true
              dialogClassName="modal-90w" // Custom class for modal
              onHide={() => setShowPopupCustomInput(false)} // Hide modal when the close Button is clicked
              size="xl" // Extra large modal
              aria-labelledby="contained-modal-title-vcenter" // Accessibility label
              centered // Centers the modal vertically in the viewport
            >
              <Modal.Header closeButton></Modal.Header>
              <Modal.Body>
                <div>Compiler Error</div>

                {/* Textarea for displaying the compiler errors */}
                <div className="row">
                  <div className="col-12 col1">
                    <textarea
                      className="textarea1"
                      value={customResult?.compilation_errors} // Display the compiler errors
                      style={{
                        color: "red",
                        backgroundColor: "black",
                        width: "100%",
                        height: "200px",
                      }}
                      disabled // Disable user input
                    ></textarea>
                  </div>
                </div>

                <div>Runtime Error</div>

                {/* Textarea for displaying the runtime errors */}
                <div className="row">
                  <div className="col-12 col1">
                    <textarea
                      className="textarea1"
                      value={customResult?.runtime_errors} // Display the runtime errors
                      style={{
                        color: "red",
                        backgroundColor: "black",
                        width: "100%",
                        height: "200px",
                      }}
                      disabled // Disable user input
                    ></textarea>
                  </div>
                </div>
              </Modal.Body>
            </Modal>

            {/* Button for running the submitted file */}
            <div className="row">
              <Button
                style={{ marginLeft: "14px" }}
                className="primary"
                onClick={RunCustomInput} // Run the custom input on click
              >
                Run Submitted File
              </Button>

              {/* Display the evaluation status while the evaluation is in progress */}
              <div className="col-2">
                {isFetchingCustom ? (
                  <div>
                    {evaluationStatus != null ? (
                      <p style={{ color: "red" }}>{evaluationStatus}</p> // Display the evaluation status
                    ) : (
                      <div></div>
                    )}
                  </div>
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          </Card.Body>
        ) : null}
      </Card>

    </div>
  );
}

ManageSubmission.defaultProps = {
  programId: "",
  changeAccess: () => { },
  details: {},
  access: {},
  activity_title: "",
  activity_description: "",
  delegate_student_id: null,
};
